import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useFarm } from '../context/FarmContext';
import { authFetch } from '../utils/authFetch';
import '../styles/admin-pages.css';
import { isFarmLeadRole, isGlobalRole, normalizeRole } from '../utils/roles';

const FARM_PLAN_TYPES = ['included', 'basic', 'standard', 'pro', 'enterprise'];

export default function FarmsAdminPage() {
  const { t } = useLang();
  const { token, user, ready, logout } = useAuth();
  const { setFarmContextId } = useFarm();
  const navigate = useNavigate();
  const params = useParams();
  const [farms, setFarms] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState('');
  const [editOwnerId, setEditOwnerId] = useState('');
  const [editPlanType, setEditPlanType] = useState('included');
  const [updating, setUpdating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const role = normalizeRole(user?.role);
  const canManageFarms = isGlobalRole(role) || isFarmLeadRole(role);
  const canDeleteFarms = isGlobalRole(role);
  const canCreateFarms = isGlobalRole(role);

  const handleUnauthorized = useCallback(() => {
    logout();
  }, [logout]);

  const loadFarms = useCallback(async () => {
    if (!token || !canManageFarms) return;
    setLoading(true);
    setError('');
    try {
      const res = await authFetch('/api/farms', {}, { token, onUnauthorized: handleUnauthorized });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || t('farmsAdmin.loadError'));
        setFarms([]);
        return;
      }
      const data = await res.json();
      setFarms(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError(t('farmsAdmin.loadError'));
    } finally {
      setLoading(false);
    }
  }, [token, canManageFarms, handleUnauthorized, t]);

  const loadUsers = useCallback(async () => {
    if (!token || !isGlobalRole(role)) return;
    setUsersLoading(true);
    try {
      const res = await authFetch('/api/admin/users', {}, { token, onUnauthorized: handleUnauthorized });
      if (!res.ok) {
        setUsers([]);
        return;
      }
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (_) {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [token, role, handleUnauthorized]);

  useEffect(() => {
    if (ready && token && canManageFarms) loadFarms();
  }, [ready, token, canManageFarms, loadFarms]);

  useEffect(() => {
    if (ready && token && isGlobalRole(role)) loadUsers();
  }, [ready, token, role, loadUsers]);

  useEffect(() => {
    if (!params.id || !farms.length || !isGlobalRole(role)) return;
    const target = farms.find((f) => String(f._id) === String(params.id));
    if (!target) return;
    setEditTarget(target);
    setEditName(target.name || '');
    setEditOwnerId(target.ownerId ? String(target.ownerId) : '');
    setEditPlanType(target.planType && FARM_PLAN_TYPES.includes(target.planType) ? target.planType : 'included');
  }, [params.id, farms, role]);

  if (!ready) {
    return (
      <div className="container mt-3">
        <span className="spinner-border spinner-border-sm me-2" />
        {t('farmsAdmin.loading')}
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!canManageFarms) {
    return (
      <div className="container mt-3">
        <div className="alert alert-warning">{t('farmsAdmin.accessDenied')}</div>
      </div>
    );
  }

  const submitCreate = async (e) => {
    e.preventDefault();
    const trimmed = newName.trim();
    if (!trimmed) return;
    setCreating(true);
    try {
      const res = await authFetch(
        '/api/farms',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: trimmed }),
        },
        { token, onUnauthorized: handleUnauthorized }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || t('farmsAdmin.createFailed'));
        return;
      }
      setNewName('');
      setCreateOpen(false);
      await loadFarms();
    } catch (err) {
      console.error(err);
      setError(t('farmsAdmin.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  const openEditModal = (farm) => {
    setEditTarget(farm);
    setEditName(farm?.name || '');
    setEditOwnerId(farm?.ownerId ? String(farm.ownerId) : '');
    setEditPlanType(
      farm?.planType && FARM_PLAN_TYPES.includes(farm.planType) ? farm.planType : 'included'
    );
    setError('');
  };

  const closeEditModal = () => {
    setEditTarget(null);
    setEditName('');
    setEditOwnerId('');
    setEditPlanType('included');
    if (params.id) navigate('/admin/farms');
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editTarget) return;
    const trimmed = editName.trim();
    if (!trimmed) return;
    setUpdating(true);
    try {
      const res = await authFetch(
        `/api/admin/farms/${encodeURIComponent(editTarget._id)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: trimmed,
            ownerId: editOwnerId || null,
            planType: editPlanType,
          }),
        },
        { token, onUnauthorized: handleUnauthorized }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || t('farmsAdmin.updateFailed'));
        return;
      }
      await loadFarms();
      closeEditModal();
    } catch (err) {
      console.error(err);
      setError(t('farmsAdmin.updateFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await authFetch(
        `/api/farms/${deleteTarget._id}`,
        { method: 'DELETE' },
        { token, onUnauthorized: handleUnauthorized }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || t('farmsAdmin.deleteFailed'));
        return;
      }
      setDeleteTarget(null);
      loadFarms();
    } catch (err) {
      console.error(err);
      setError(t('farmsAdmin.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="container mt-3 admin-page">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <h1 className="mb-0">{t('farmsAdmin.title')}</h1>
        {canCreateFarms ? (
          <button type="button" className="btn btn-ios btn-ios--primary" onClick={() => setCreateOpen(true)}>
            {t('farmsAdmin.create')}
          </button>
        ) : null}
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      {loading ? (
        <p className="text-muted">{t('farmsAdmin.loading')}</p>
      ) : farms.length === 0 ? (
        <p className="text-muted">{t('farmsAdmin.empty')}</p>
      ) : (
        <div className="d-none d-md-block table-responsive glass-panel p-0 overflow-hidden">
          <table className="table table-striped table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>{t('farmsAdmin.colName')}</th>
                <th>{t('farmsAdmin.colOwner')}</th>
                <th className="text-end">{t('farmsAdmin.colUsers')}</th>
                <th className="text-end">{t('farmsAdmin.colDevices')}</th>
                <th className="text-end">{t('farmsAdmin.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {farms.map((f) => (
                <tr key={String(f._id)}>
                  <td>
                    <Link to={`/admin/farms/${encodeURIComponent(String(f._id))}`} className="fw-semibold text-decoration-none">
                      {f.name}
                    </Link>
                  </td>
                  <td>{f.ownerEmail || '—'}</td>
                  <td className="text-end">{f.userCount ?? 0}</td>
                  <td className="text-end">{f.deviceCount ?? 0}</td>
                  <td className="text-end">
                    <div className="d-inline-flex gap-2">
                      {isGlobalRole(role) ? (
                        <button type="button" className="btn btn-ios btn-ios--muted btn-sm" onClick={() => openEditModal(f)}>
                          {t('farmsAdmin.edit')}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn-ios btn-ios--muted btn-sm"
                        onClick={() => {
                          setFarmContextId(String(f._id));
                          navigate('/users');
                        }}
                      >
                        {t('farmsAdmin.usersAction')}
                      </button>
                      {canDeleteFarms ? (
                        <button
                          type="button"
                          className="btn btn-ios btn-ios--destructive btn-sm"
                          onClick={() => setDeleteTarget(f)}
                        >
                          {t('farmsAdmin.delete')}
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && farms.length > 0 ? (
        <div className="d-md-none">
          {farms.map((f) => (
            <div key={String(f._id)} className="admin-mobile-card">
              <div className="admin-mobile-card__label">{t('farmsAdmin.colName')}</div>
              <div className="fw-semibold">
                <Link to={`/admin/farms/${encodeURIComponent(String(f._id))}`} className="text-decoration-none">
                  {f.name}
                </Link>
              </div>
              <div className="admin-mobile-card__label mt-2">{t('farmsAdmin.colOwner')}</div>
              <div>{f.ownerEmail || '—'}</div>
              <div className="admin-mobile-card__row mt-2">
                <span className="text-muted small">
                  {t('farmsAdmin.colUsers')}: <strong className="text-white">{f.userCount ?? 0}</strong>
                </span>
                <span className="text-muted small">
                  {t('farmsAdmin.colDevices')}: <strong className="text-white">{f.deviceCount ?? 0}</strong>
                </span>
              </div>
              <div className="admin-mobile-card__actions">
                {isGlobalRole(role) ? (
                  <button type="button" className="btn btn-ios btn-ios--muted" onClick={() => openEditModal(f)}>
                    {t('farmsAdmin.edit')}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn btn-ios btn-ios--muted"
                  onClick={() => {
                    setFarmContextId(String(f._id));
                    navigate('/users');
                  }}
                >
                  {t('farmsAdmin.usersAction')}
                </button>
                {canDeleteFarms ? (
                  <button type="button" className="btn btn-ios btn-ios--destructive" onClick={() => setDeleteTarget(f)}>
                    {t('farmsAdmin.delete')}
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {createOpen && canCreateFarms && (
        <div
          className="modal d-block"
          tabIndex="-1"
          role="dialog"
          onClick={() => !creating && setCreateOpen(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            role="document"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <form onSubmit={submitCreate}>
                <div className="modal-header">
                  <h5 className="modal-title">{t('farmsAdmin.modalTitle')}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    disabled={creating}
                    onClick={() => setCreateOpen(false)}
                    aria-label={t('cancel')}
                  />
                </div>
                <div className="modal-body">
                  <label className="form-label">{t('farmsAdmin.farmNamePlaceholder')}</label>
                  <input
                    className="form-control"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    autoFocus
                    required
                  />
                </div>
                <div className="modal-footer gap-2">
                  <button
                    type="button"
                    className="btn btn-ios btn-ios--muted"
                    disabled={creating}
                    onClick={() => setCreateOpen(false)}
                  >
                    {t('cancel')}
                  </button>
                  <button type="submit" className="btn btn-ios btn-ios--primary" disabled={creating}>
                    {creating ? t('farmsAdmin.creating') : t('farmsAdmin.createSubmit')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && canDeleteFarms && (
        <div className="modal d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('farmsAdmin.confirmDeleteTitle')}</h5>
                <button
                  type="button"
                  className="btn-close"
                  disabled={deleting}
                  onClick={() => setDeleteTarget(null)}
                  aria-label={t('cancel')}
                />
              </div>
              <div className="modal-body">
                <p className="mb-0">{t('farmsAdmin.confirmDelete', deleteTarget.name)}</p>
              </div>
              <div className="modal-footer gap-2">
                <button
                  type="button"
                  className="btn btn-ios btn-ios--muted"
                  disabled={deleting}
                  onClick={() => setDeleteTarget(null)}
                >
                  {t('cancel')}
                </button>
                <button type="button" className="btn btn-ios btn-ios--destructive" disabled={deleting} onClick={confirmDelete}>
                  {deleting ? t('farmsAdmin.deleting') : t('farmsAdmin.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editTarget && isGlobalRole(role) && (
        <div className="modal d-block" tabIndex="-1" role="dialog" onClick={() => !updating && closeEditModal()}>
          <div className="modal-dialog modal-dialog-centered" role="document" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <form onSubmit={submitEdit}>
                <div className="modal-header">
                  <h5 className="modal-title">{t('farmsAdmin.editTitle')}</h5>
                  <button type="button" className="btn-close" disabled={updating} onClick={closeEditModal} aria-label={t('cancel')} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">{t('farmsAdmin.farmNamePlaceholder')}</label>
                    <input className="form-control" value={editName} onChange={(e) => setEditName(e.target.value)} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">{t('farmsAdmin.colOwner')}</label>
                    <select className="form-select" value={editOwnerId} onChange={(e) => setEditOwnerId(e.target.value)} disabled={usersLoading}>
                      <option value="">{t('farmsAdmin.ownerUnassigned')}</option>
                      {users.map((u) => (
                        <option key={String(u._id)} value={String(u._id)}>
                          {u.email}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">{t('farmsAdmin.planTypeLabel')}</label>
                    <select
                      className="form-select"
                      value={editPlanType}
                      onChange={(e) => setEditPlanType(e.target.value)}
                    >
                      {FARM_PLAN_TYPES.map((pt) => (
                        <option key={pt} value={pt}>
                          {t(`planTier.${pt}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer gap-2">
                  <button type="button" className="btn btn-ios btn-ios--muted" disabled={updating} onClick={closeEditModal}>
                    {t('cancel')}
                  </button>
                  <button type="submit" className="btn btn-ios btn-ios--primary" disabled={updating}>
                    {updating ? t('farmsAdmin.savingChanges') : t('farmsAdmin.saveChanges')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
